"use client";

/**
 * useDFlowRealtimePrice - Real-time price streaming for Kalshi markets via DFlow
 *
 * Subscribes to `kalshi-market:{ticker}` channels on the shared backend WebSocket.
 * Receives `dflow:price:update` events with bid/ask data.
 * Reuses the singleton socket from usePriceStream (no duplicate connections).
 *
 * 100% WS — no REST polling. The backend emits the last cached price on
 * subscribe (websocket-server.ts), so initial paint is instant; subsequent
 * ticks arrive over the same socket as the DFlow upstream feed publishes.
 * If the socket drops, Socket.IO auto-reconnects and `handleConnect`
 * re-issues the subscribe.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { getSharedPriceSocket, getOrCreateSharedSocket, acquireSharedSocket, releaseSharedSocket } from "./usePriceStream";

export interface DFlowPriceData {
  ticker: string;
  yesBid: number | null;
  yesAsk: number | null;
  noBid: number | null;
  noAsk: number | null;
  timestamp: number;
}

interface UseDFlowRealtimePriceOptions {
  /** Kalshi market tickers to subscribe to */
  tickers: string[];
  /** Whether to auto-connect (default: true) */
  enabled?: boolean;
}

interface UseDFlowRealtimePriceReturn {
  /** Map of ticker -> current price data */
  prices: Map<string, DFlowPriceData>;
  /** Whether the socket is connected */
  isConnected: boolean;
  /** Get price for a specific ticker */
  getPrice: (ticker: string) => DFlowPriceData | undefined;
}

export function useDFlowRealtimePrice(
  options: UseDFlowRealtimePriceOptions
): UseDFlowRealtimePriceReturn {
  const { tickers, enabled = true } = options;
  const [prices, setPrices] = useState<Map<string, DFlowPriceData>>(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const subscribedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled || tickers.length === 0) return;

    let isMounted = true;
    let socket: ReturnType<typeof getSharedPriceSocket> = null;

    const normalizedTickers = Array.from(
      new Set(tickers.map((t) => t.toUpperCase()).filter(Boolean)),
    );
    const channels = normalizedTickers.map((t) => `kalshi-market:${t}`);

    const handleConnect = () => {
      setIsConnected(true);
      // Socket.IO auto-reconnects on drop, but the server forgets which
      // channels we had subscribed to. Re-emit on every (re)connection so
      // prices resume publishing without a page refresh.
      if (socket && channels.length > 0) {
        socket.emit("subscribe", channels);
        subscribedRef.current = new Set(channels);
      }
    };
    const handleDisconnect = () => setIsConnected(false);

    const handlePriceUpdate = (data: DFlowPriceData) => {
      if (!data?.ticker) return;
      setPrices((prev) => {
        const next = new Map(prev);
        next.set(data.ticker.toUpperCase(), {
          ...data,
          ticker: data.ticker.toUpperCase(),
        });
        return next;
      });
    };

    const init = async () => {
      socket = getSharedPriceSocket() || await getOrCreateSharedSocket();
      if (!socket || !isMounted) return;

      acquireSharedSocket();

      socket.on("connect", handleConnect);
      socket.on("disconnect", handleDisconnect);
      socket.on("dflow:price:update", handlePriceUpdate);
      if (socket.connected) {
        setIsConnected(true);
        socket.emit("subscribe", channels);
        subscribedRef.current = new Set(channels);
      }
    };

    init();

    return () => {
      isMounted = false;
      if (socket) {
        if (subscribedRef.current.size > 0) {
          socket.emit("unsubscribe", [...subscribedRef.current]);
          subscribedRef.current.clear();
        }
        socket.off("connect", handleConnect);
        socket.off("disconnect", handleDisconnect);
        socket.off("dflow:price:update", handlePriceUpdate);
        releaseSharedSocket();
      }
    };
  }, [enabled, tickers.join(",")]);

  const getPrice = useCallback(
    (ticker: string) => prices.get(ticker.toUpperCase()),
    [prices]
  );

  return { prices, isConnected, getPrice };
}
